import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Space,
  Typography,
  Input,
  Select,
  Modal,
  Form as AntForm,
  message,
  Row,
  Col,
  Switch,
  Tooltip,
  Badge,
  Upload,
  Spin,
  Card,
  Radio,
  Divider,
  Alert,
  TimePicker
} from "antd";
import {
  EyeOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  GlobalOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  FieldTimeOutlined,
  FormOutlined,
  CopyFilled,
  CloseOutlined,
  CheckCircleOutlined,
  ReconciliationOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import FormBuilderContent from "./FormBuilderContent";
import FieldTypesPalette, { CompactFieldTypesPalette } from "./FieldTypesPalette";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useTranslation } from '../hooks/useTranslation';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const BREAKPOINT = 768;
const LS_KEY = "dma_sb_collapsed";

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: "custom", label: "Custom / सानुकूल", icon: <FieldTimeOutlined /> },
];

const ScheduleConfiguration = ({ 
  frequencyType, 
  scheduleConfig, 
  onScheduleChange, 
  language,
  disabled 
}) => {
  const DATE_FORMATS = [
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2024-01-31)" },
    { value: "DD-MM-YYYY", label: "DD-MM-YYYY (31-01-2024)" },
    { value: "MM-DD-YYYY", label: "MM-DD-YYYY (01-31-2024)" },
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY (31/01/2024)" },
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY (01/31/2024)" },
  ];

  const MONTHLY_DATE_FORMATS = [
    { value: "YYYY-MM", label: "YYYY-MM " },
    { value: "MM-YYYY", label: "MM-YYYY " },
    { value: "MMM-YYYY", label: "MMM-YYYY " },
    { value: "MMMM-YYYY", label: "MMMM-YYYY " },
  ];

  const YEARLY_FORMATS = [
    { value: "YY", label: "YY " },
    { value: "YYYY", label: "YYYY" },
    {value: "YY-YY", label: "YY-YY" },
    { value: "YYYY-YY", label: "YYYY-YY" },
    { value: "YYYY/YYYY", label: "YYYY/YYYY" },
    { value: "YYYY-YYYY", label: "YYYY-YYYY Financial Year" },
    { value: "FY-YYYY", label: "FY-YYYY " },
  ];

  const WEEK_NUMBERS = [
    { value: 1, label: "1st " },
    { value: 2, label: "2nd " },
    { value: 3, label: "3rd " },
    { value: 4, label: "4th " },
    { value: "last", label: "Last" },
  ];

  const PERIOD_OPTIONS = [
    { value: "last", label: "Last ", example: "example :Last month " },
    { value: "this", label: "This ", example: "example :This month " },
    { value: "next", label: "Next ", example: "example :Next month " },
  ];

  const WEEKDAYS = [
    { value: "monday", label: "Monday ", short: "Mon" },
    { value: "tuesday", label: "Tuesday ", short: "Tue" },
    { value: "wednesday", label: "Wednesday ", short: "Wed" },
    { value: "thursday", label: "Thursday ", short: "Thu" },
    { value: "friday", label: "Friday ", short: "Fri" },
    { value: "saturday", label: "Saturday ", short: "Sat" },
    { value: "sunday", label: "Sunday ", short: "Sun" },
  ];

  const MONTHS = [
    { value: 1, label: "January " },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May " },
    { value: 6, label: "June " },
    { value: 7, label: "July" },
    { value: 8, label: "August " },
    { value: 9, label: "September " },
    { value: 10, label: "October " },
    { value: 11, label: "November " },
    { value: 12, label: "December " },
  ];

  const monthlySelectionType = scheduleConfig?.monthlySelectionType || "date";
  const selectionType = scheduleConfig?.selectionType || "date";

  const updateSelectionType = (type) => {
    if (frequencyType === 'monthly') {
      onScheduleChange({ ...scheduleConfig, monthlySelectionType: type });
    } else {
      onScheduleChange({ ...scheduleConfig, selectionType: type });
    }
  };

  const handleTimeChange = (time) => {
    if (time && time.isValid()) {
      const formattedTime = time.format('HH:mm');
      onScheduleChange({ ...scheduleConfig, time: formattedTime });
    }
  };

  const getEveryNMonths = () => {
    if (frequencyType === 'quarterly') {
      return scheduleConfig?.quarterlyEveryNMonths ?? 3;
    }
    if (frequencyType === 'half_yearly') {
      return scheduleConfig?.halfYearlyEveryNMonths ?? 6;
    }
    return scheduleConfig?.everyNMonths ?? 3;
  };

  const handleEveryNMonthsChange = (value) => {
    const numValue = parseInt(value) || (frequencyType === 'quarterly' ? 3 : 6);
    if (frequencyType === 'quarterly') {
      onScheduleChange({ ...scheduleConfig, quarterlyEveryNMonths: numValue });
    } else if (frequencyType === 'half_yearly') {
      onScheduleChange({ ...scheduleConfig, halfYearlyEveryNMonths: numValue });
    } else {
      onScheduleChange({ ...scheduleConfig, everyNMonths: numValue });
    }
  };

  const everyNMonths = getEveryNMonths();
  const timeValue = scheduleConfig?.time || "09:00";
  const selectedWeekday = scheduleConfig?.weekday || "monday";
  const selectedDate = scheduleConfig?.date || 1;
  const selectedMonth = scheduleConfig?.month || 1;
  const selectedWeekNumber = scheduleConfig?.weekNumber || 1;
  const selectedDayOfWeek = scheduleConfig?.dayOfWeek || "monday";
  const dateFormat = scheduleConfig?.dateFormat || "YYYY-MM-DD";
  const monthlyDateFormat = scheduleConfig?.monthlyDateFormat || "YYYY-MM";
  const yearlyFormat = scheduleConfig?.yearlyFormat || "YYYY";
  const period = scheduleConfig?.period || "this";

  if (frequencyType === 'daily') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <FieldTimeOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Daily Schedule Configuration' : 'दैनिक वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {language === 'english' ? 'Daily at this time' : 'दररोज या वेळी'}
            </Text>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={dateFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, dateFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {DATE_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>{format.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {language === 'english' 
                ? `Daily at ${timeValue} (${period} period)`
                : `दररोज ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`}
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  if (frequencyType === 'weekly') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <FieldTimeOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Weekly Schedule Configuration' : 'साप्ताहिक वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Day of Week:' : 'आठवड्याचा दिवस:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={selectedWeekday}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, weekday: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {WEEKDAYS.map(day => (
                <Option key={day.value} value={day.value}>{day.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={dateFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, dateFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Option value="day YYYY-MM-DD">Day YYYY-MM-DD (Monday 2024-01-31)</Option>
              <Option value="day DD-MM-YYYY">Day DD-MM-YYYY (Monday 31-01-2024)</Option>
              <Option value="YYYY-MM-DD">YYYY-MM-DD (2024-01-31)</Option>
              <Option value="DD-MM-YYYY">DD-MM-YYYY (31-01-2024)</Option>
              <Option value="MM-DD-YYYY">MM-DD-YYYY (01-31-2024)</Option>
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {language === 'english'
                ? `Every ${WEEKDAYS.find(d => d.value === selectedWeekday)?.label} at ${timeValue} (${period} period)`
                : `दर ${WEEKDAYS.find(d => d.value === selectedWeekday)?.label} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`}
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  if (frequencyType === 'monthly') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <FieldTimeOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Monthly Schedule Configuration' : 'मासिक वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Select Type:' : 'निवड प्रकार:'}</Text>
          </Col>
          <Col span={16}>
            <Radio.Group
              value={monthlySelectionType}
              onChange={(e) => updateSelectionType(e.target.value)}
              disabled={disabled}
            >
              <Radio value="date">
                <Space><CalendarOutlined /> {language === 'english' ? 'Select as Date' : 'तारीख म्हणून निवडा'}</Space>
              </Radio>
              <Radio value="day">
                <Space><FieldTimeOutlined /> {language === 'english' ? 'Select as Day' : 'दिवस म्हणून निवडा'}</Space>
              </Radio>
            </Radio.Group>
          </Col>
        </Row>

        {monthlySelectionType === 'date' && (
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{language === 'english' ? 'Date of Month:' : 'महिन्याची तारीख:'}</Text>
            </Col>
            <Col span={10}>
              <Select
                style={{ width: '100%' }}
                value={selectedDate}
                onChange={(value) => onScheduleChange({ ...scheduleConfig, date: value })}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <Option key={day} value={day}>{day}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}

        {monthlySelectionType === 'day' && (
          <>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Day of Week:' : 'आठवड्याचा दिवस:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedDayOfWeek}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, dayOfWeek: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEKDAYS.map(day => (
                    <Option key={day.value} value={day.value}>{day.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Week Number:' : 'आठवडा क्रमांक:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedWeekNumber}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, weekNumber: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEK_NUMBERS.map(week => (
                    <Option key={week.value} value={week.value}>{week.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={monthlyDateFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, monthlyDateFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {MONTHLY_DATE_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>{format.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {monthlySelectionType === 'date' 
                ? (language === 'english'
                    ? `Every month on day ${selectedDate} at ${timeValue} (${period} period)`
                    : `दर महिन्याला तारखेला ${selectedDate} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
                : (language === 'english'
                    ? `Every month on the ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} at ${timeValue} (${period} period)`
                    : `दर महिन्याला ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
              }
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  if (frequencyType === 'quarterly') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <ReconciliationOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Quarterly Schedule Configuration' : 'त्रैमासिक वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Select Type:' : 'निवड प्रकार:'}</Text>
          </Col>
          <Col span={16}>
            <Radio.Group
              value={selectionType}
              onChange={(e) => updateSelectionType(e.target.value)}
              disabled={disabled}
            >
              <Radio value="date">
                <Space><CalendarOutlined /> {language === 'english' ? 'Select as Date' : 'तारीख म्हणून निवडा'}</Space>
              </Radio>
              <Radio value="day">
                <Space><FieldTimeOutlined /> {language === 'english' ? 'Select as Day' : 'दिवस म्हणून निवडा'}</Space>
              </Radio>
            </Radio.Group>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Every:' : 'प्रत्येक:'}</Text>
          </Col>
          <Col span={10}>
            <Input
              type="number"
              min={1}
              value={everyNMonths}
              onChange={(e) => handleEveryNMonthsChange(e.target.value)}
              disabled={disabled}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {language === 'english' ? 'month(s)' : 'महिने'}
            </Text>
          </Col>
        </Row>

        {selectionType === 'date' && (
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{language === 'english' ? 'Date of Month:' : 'महिन्याची तारीख:'}</Text>
            </Col>
            <Col span={10}>
              <Select
                style={{ width: '100%' }}
                value={selectedDate}
                onChange={(value) => onScheduleChange({ ...scheduleConfig, date: value })}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <Option key={day} value={day}>{day}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}

        {selectionType === 'day' && (
          <>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Day of Week:' : 'आठवड्याचा दिवस:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedDayOfWeek}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, dayOfWeek: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEKDAYS.map(day => (
                    <Option key={day.value} value={day.value}>{day.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Week Number:' : 'आठवडा क्रमांक:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedWeekNumber}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, weekNumber: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEK_NUMBERS.map(week => (
                    <Option key={week.value} value={week.value}>{week.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={monthlyDateFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, monthlyDateFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {MONTHLY_DATE_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>{format.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {selectionType === 'date'
                ? (language === 'english'
                    ? `Every ${everyNMonths} months on day ${selectedDate} at ${timeValue} (${period} period)`
                    : `दर ${everyNMonths} महिन्याला तारखेला ${selectedDate} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
                : (language === 'english'
                    ? `Every ${everyNMonths} months on the ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} at ${timeValue} (${period} period)`
                    : `दर ${everyNMonths} महिन्याला ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
              }
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  if (frequencyType === 'half_yearly') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <ReconciliationOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Half-Yearly Schedule Configuration' : 'सहामाही वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Select Type:' : 'निवड प्रकार:'}</Text>
          </Col>
          <Col span={16}>
            <Radio.Group
              value={selectionType}
              onChange={(e) => updateSelectionType(e.target.value)}
              disabled={disabled}
            >
              <Radio value="date">
                <Space><CalendarOutlined /> {language === 'english' ? 'Select as Date' : 'तारीख म्हणून निवडा'}</Space>
              </Radio>
              <Radio value="day">
                <Space><FieldTimeOutlined /> {language === 'english' ? 'Select as Day' : 'दिवस म्हणून निवडा'}</Space>
              </Radio>
            </Radio.Group>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Every:' : 'प्रत्येक:'}</Text>
          </Col>
          <Col span={10}>
            <Input
              type="number"
              min={1}
              value={everyNMonths}
              onChange={(e) => handleEveryNMonthsChange(e.target.value)}
              disabled={disabled}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {language === 'english' ? 'month(s)' : 'महिने'}
            </Text>
          </Col>
        </Row>

        {selectionType === 'date' && (
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Text strong>{language === 'english' ? 'Date of Month:' : 'महिन्याची तारीख:'}</Text>
            </Col>
            <Col span={10}>
              <Select
                style={{ width: '100%' }}
                value={selectedDate}
                onChange={(value) => onScheduleChange({ ...scheduleConfig, date: value })}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <Option key={day} value={day}>{day}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}

        {selectionType === 'day' && (
          <>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Day of Week:' : 'आठवड्याचा दिवस:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedDayOfWeek}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, dayOfWeek: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEKDAYS.map(day => (
                    <Option key={day.value} value={day.value}>{day.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Week Number:' : 'आठवडा क्रमांक:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedWeekNumber}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, weekNumber: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEK_NUMBERS.map(week => (
                    <Option key={week.value} value={week.value}>{week.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={monthlyDateFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, monthlyDateFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {MONTHLY_DATE_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>{format.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {selectionType === 'date'
                ? (language === 'english'
                    ? `Every ${everyNMonths} months on day ${selectedDate} at ${timeValue} (${period} period)`
                    : `दर ${everyNMonths} महिन्याला तारखेला ${selectedDate} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
                : (language === 'english'
                    ? `Every ${everyNMonths} months on the ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} at ${timeValue} (${period} period)`
                    : `दर ${everyNMonths} महिन्याला ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
              }
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  if (frequencyType === 'yearly') {
    return (
      <div style={{
        marginTop: 16,
        padding: 20,
        background: "#f0f5ff",
        borderRadius: 8,
        border: "1px solid #adc6ff"
      }}>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <FieldTimeOutlined style={{ color: "#1890ff" }} />
            <Text strong>
              {language === 'english' ? 'Yearly Schedule Configuration' : 'वार्षिक वेळापत्रक कॉन्फिगरेशन'}
            </Text>
          </Space>
        </div>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Select Type:' : 'निवड प्रकार:'}</Text>
          </Col>
          <Col span={16}>
            <Radio.Group
              value={selectionType}
              onChange={(e) => updateSelectionType(e.target.value)}
              disabled={disabled}
            >
              <Radio value="date">
                <Space><CalendarOutlined /> {language === 'english' ? 'Select as Date' : 'तारीख म्हणून निवडा'}</Space>
              </Radio>
              <Radio value="day">
                <Space><FieldTimeOutlined /> {language === 'english' ? 'Select as Day' : 'दिवस म्हणून निवडा'}</Space>
              </Radio>
            </Radio.Group>
          </Col>
        </Row>

        {selectionType === 'date' && (
          <>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Date of Month:' : 'महिन्याची तारीख:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedDate}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, date: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <Option key={day} value={day}>{day}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Month:' : 'महिना:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedMonth}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, month: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {MONTHS.map(month => (
                    <Option key={month.value} value={month.value}>{month.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        {selectionType === 'day' && (
          <>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Day of Week:' : 'आठवड्याचा दिवस:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedDayOfWeek}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, dayOfWeek: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEKDAYS.map(day => (
                    <Option key={day.value} value={day.value}>{day.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Week Number:' : 'आठवडा क्रमांक:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedWeekNumber}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, weekNumber: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {WEEK_NUMBERS.map(week => (
                    <Option key={week.value} value={week.value}>{week.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>

            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Text strong>{language === 'english' ? 'Month:' : 'महिना:'}</Text>
              </Col>
              <Col span={10}>
                <Select
                  style={{ width: '100%' }}
                  value={selectedMonth}
                  onChange={(value) => onScheduleChange({ ...scheduleConfig, month: value })}
                  disabled={disabled}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {MONTHS.map(month => (
                    <Option key={month.value} value={month.value}>{month.label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </>
        )}

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Time:' : 'वेळ:'}</Text>
          </Col>
          <Col span={10}>
            <div style={{ position: 'relative', zIndex: 9999 }}>
              <TimePicker
                value={timeValue ? moment(timeValue, 'HH:mm') : null}
                onChange={handleTimeChange}
                format="HH:mm"
                style={{ width: '100%' }}
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
          </Col>
        </Row>

        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Format:' : 'स्वरूप:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={yearlyFormat}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, yearlyFormat: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {YEARLY_FORMATS.map(format => (
                <Option key={format.value} value={format.value}>{format.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row align="middle" gutter={16}>
          <Col span={6}>
            <Text strong>{language === 'english' ? 'Period:' : 'कालावधी:'}</Text>
          </Col>
          <Col span={10}>
            <Select
              style={{ width: '100%' }}
              value={period}
              onChange={(value) => onScheduleChange({ ...scheduleConfig, period: value })}
              disabled={disabled}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                    ({opt.example.replace('month', 'year')})
                  </span>
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div style={{
          marginTop: 20,
          padding: 12,
          background: "#e6f7ff",
          borderRadius: 6,
          border: "1px solid #91d5ff"
        }}>
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text strong>{language === 'english' ? 'Schedule Summary:' : 'वेळापत्रक सारांश:'}</Text>
            <Text>
              {selectionType === 'date'
                ? (language === 'english'
                    ? `Every year on ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedDate} at ${timeValue} (${period} period)`
                    : `दरवर्षी ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedDate} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
                : (language === 'english'
                    ? `Every year on the ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} of ${MONTHS.find(m => m.value === selectedMonth)?.label} at ${timeValue} (${period} period)`
                    : `दरवर्षी ${MONTHS.find(m => m.value === selectedMonth)?.label} महिन्यातील ${WEEK_NUMBERS.find(w => w.value === selectedWeekNumber)?.label} ${WEEKDAYS.find(d => d.value === selectedDayOfWeek)?.label} ${timeValue} वाजता (${period === 'last' ? 'मागील' : period === 'this' ? 'हा' : 'पुढील'} कालावधी)`)
              }
            </Text>
          </Space>
        </div>
      </div>
    );
  }

  return null;
};

function FormBuilderMain() {
  const location = useLocation();

  const {
    translateField,
    translateForm,
    translateTyping,
    translating,
    autoTranslate,
    setAutoTranslate
  } = useTranslation();

  const [activeTab, setActiveTab] = useState("form-builder");
  const [formName, setFormName] = useState("Untitled Document");
  const [saving, setSaving] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [language, setLanguage] = useState("english");

  const formBuilderContainerRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= BREAKPOINT);

  const [sbCollapsed, setSbCollapsed] = useState(() => {
    const saved = sessionStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  const [formIcon, setFormIcon] = useState(null);
  const [formIconUrl, setFormIconUrl] = useState('');
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconLoadError, setIconLoadError] = useState(false);

  const [scheduleConfig, setScheduleConfig] = useState({
    time: "09:00",
    period: "this",
    dateFormat: "YYYY-MM-DD",
    weekday: "monday",
    monthlySelectionType: "date",
    date: 1,
    dayOfWeek: "monday",
    weekNumber: 1,
    monthlyDateFormat: "YYYY-MM",
    quarterlyEveryNMonths: 3,
    halfYearlyEveryNMonths: 6,
    everyNMonths: 3,
    selectionType: "date",
    month: 1,
    yearlyFormat: "YYYY",
  });

  const handleScheduleConfigChange = (newScheduleConfig) => {
    setScheduleConfig(prev => ({
      ...prev,
      ...newScheduleConfig
    }));
  };

  const toggleSidebar = () => {
    const newState = !sbCollapsed;
    setSbCollapsed(newState);
    sessionStorage.setItem(LS_KEY, JSON.stringify(newState));
    window.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new StorageEvent('storage', {
      key: LS_KEY,
      newValue: JSON.stringify(newState),
      oldValue: JSON.stringify(sbCollapsed)
    }));
  };

  const [formsList, setFormsList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedFormId, setSelectedFormId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [workflow, setWorkflow] = useState({ type: "none", sequence: [] });
  const [sla, setSla] = useState({});

  const [existingColumns, setExistingColumns] = useState([]);
  const [showExistingPalette, setShowExistingPalette] = useState(false);

  const [formLanguageMode, setFormLanguageMode] = useState('bilingual');

  const [selectedULBClasses, setSelectedULBClasses] = useState([]);
  const [ulbList, setUlbList] = useState([]);
  const [filteredUlbs, setFilteredUlbs] = useState([]);
  const [selectedUlbs, setSelectedUlbs] = useState([]);
  const [loadingUlbs, setLoadingUlbs] = useState(false);
  const [targetKeys, setTargetKeys] = useState([]);

  const [approvalStatus, setApprovalStatus] = useState('pending');
  const [formCreator, setFormCreator] = useState(null);
  const [formCreatedAt, setFormCreatedAt] = useState(null);
  const [reviewedBy, setReviewedBy] = useState(null);
  const [reviewedAt, setReviewedAt] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewerNotes, setReviewerNotes] = useState('');

  const [reportConfig, setReportConfig] = useState({
    reportName: '',
    reportDescription: '',
    selectedFields: [],
    operations: {}
  });

  const [entryType, setEntryType] = useState("single");
  const [enableFrequency, setEnableFrequency] = useState(false);
  const [frequencyType, setFrequencyType] = useState(null);
  const [customFrequency, setCustomFrequency] = useState("");
  const [frequencyUnit, setFrequencyUnit] = useState("days");
  const [frequencyValue, setFrequencyValue] = useState(1);
  const [maxEntriesPerUser, setMaxEntriesPerUser] = useState(0);

  const [currentUser, setCurrentUser] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_BASE = `${API_BASE_URL}/api`;

  const getFullImageUrl = (iconPath) => {
    if (!iconPath) return null;
    if (iconPath.startsWith('data:')) return iconPath;
    if (iconPath.startsWith('http://') || iconPath.startsWith('https://')) return iconPath;
    if (iconPath.startsWith('/dashboard-icons/') || iconPath.startsWith('/uploads/')) {
      const baseUrl = API_BASE_URL?.replace('/api', '') || 'https://dmarepositoryportal.in';
      return `${baseUrl}${iconPath}`;
    }
    return iconPath;
  };

  const uploadIconImmediately = async (base64Image) => {
    if (!base64Image) return null;
    setUploadingIcon(true);
    try {
      const payload = { icon_base64: base64Image, form_name: formName };
      if (selectedFormId) payload.form_id = selectedFormId;
      const response = await axios.post(`${API_BASE}/forms/upload-icon`, payload);
      if (response.data?.success) {
        const newIconUrl = response.data.data.icon_url;
        const fullUrl = getFullImageUrl(newIconUrl);
        setFormIconUrl(fullUrl);
        setFormIcon(fullUrl);
        setIconLoadError(false);
        message.success('Icon uploaded successfully!');
        return newIconUrl;
      }
      return null;
    } catch (error) {
      console.error('Icon upload failed:', error);
      message.error('Failed to upload icon: ' + (error.response?.data?.error || error.message));
      return null;
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleIconUpload = async (file) => {
    const isPNG = file.type === 'image/png';
    const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg';
    if (!isPNG && !isJPG) {
      message.error('Only PNG, JPG or JPEG files allowed');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB');
      return false;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64String = e.target.result;
      setFormIconUrl(base64String);
      setFormIcon(base64String);
      setIconLoadError(false);
      await uploadIconImmediately(base64String);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleRemoveIcon = async () => {
    if (!selectedFormId) {
      setFormIcon(null);
      setFormIconUrl('');
      setIconLoadError(false);
      message.info('Icon removed');
      return;
    }
    setUploadingIcon(true);
    try {
      await axios.put(`${API_BASE}/forms/${selectedFormId}`, {
        form_icon: null,
        form_icon_type: null,
        form_icon_emoji: null
      });
      setFormIcon(null);
      setFormIconUrl('');
      setIconLoadError(false);
      message.success('Icon removed successfully');
    } catch (error) {
      console.error('Failed to remove icon:', error);
      message.error('Failed to remove icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  useEffect(() => {
    if (location.state?.mode === "edit" && location.state?.formData) {
      const { formData } = location.state;
      setFormName(formData.form_name || "Untitled Document");
      if (formData.form_icon) {
        const fullUrl = getFullImageUrl(formData.form_icon);
        setFormIcon(fullUrl);
        setFormIconUrl(fullUrl);
      } else if (formData.form_icon_emoji) {
        setFormIcon(formData.form_icon_emoji);
        setFormIconUrl(formData.form_icon_emoji);
      }
      if (formData.schema && Array.isArray(formData.schema)) setNodes(formData.schema);
      setSelectedFormId(formData.id);
      // ✅ CRITICAL FIX: Load permissions from access_control first
      if (formData.access_control && typeof formData.access_control === 'object' && Object.keys(formData.access_control).length > 0) {
        console.log("✅ Loading permissions from access_control:", formData.access_control);
        setPermissions(formData.access_control);
      } else if (formData.permissions && typeof formData.permissions === 'object' && Object.keys(formData.permissions).length > 0) {
        console.log("✅ Loading permissions from permissions:", formData.permissions);
        setPermissions(formData.permissions);
      } else {
        setPermissions({});
      }
      setWorkflow(formData.workflow || { type: "none", sequence: [] });
      setSla(formData.sla || {});
      setLanguage(formData.language || "english");
      if (formData.form_language_mode) setFormLanguageMode(formData.form_language_mode);
      if (formData.entry_type) setEntryType(formData.entry_type);
      if (formData.approval_status) setApprovalStatus(formData.approval_status);
      if (formData.report_config) setReportConfig(formData.report_config);
      if (formData.selected_ulb_classes) setSelectedULBClasses(formData.selected_ulb_classes);
      if (formData.selected_ulbs) {
        setSelectedUlbs(formData.selected_ulbs);
        setTargetKeys(formData.selected_ulbs.map(id => id.toString()));
      }
      if (formData.frequency?.schedule) setScheduleConfig(prev => ({ ...prev, ...formData.frequency.schedule }));
      if (formData.frequency?.enabled !== undefined) setEnableFrequency(formData.frequency.enabled);
      if (formData.frequency?.type) setFrequencyType(formData.frequency.type);
    }
    else if (location.state?.mode === "new") {
      setFormName("Untitled Form");
      setNodes([]);
      setSelectedFormId(null);
      setPermissions({});
      setWorkflow({ type: "none", sequence: [] });
      setSla({});
      setEntryType("single");
      setEnableFrequency(false);
      setFrequencyType(null);
      setFormLanguageMode('bilingual');
      setSelectedULBClasses([]);
      setSelectedUlbs([]);
      setTargetKeys([]);
      setApprovalStatus('pending');
      setReportConfig({ reportName: '', reportDescription: '', selectedFields: [], operations: {} });
      setFormIcon(null);
      setFormIconUrl('');
      setIconLoadError(false);
    }
  }, [location.state]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === LS_KEY) {
        const newValue = e.newValue ? JSON.parse(e.newValue) : false;
        setSbCollapsed(newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchFormsList = async () => {
    try {
      setLoadingList(true);
      const res = await axios.get(`${API_BASE}/forms`);
      setFormsList(res.data?.data || []);
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to load forms");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchFormsList();
  }, []);

  useEffect(() => {
    const fetchUlbs = async () => {
      try {
        setLoadingUlbs(true);
        const response = await axios.get(`${API_BASE}/ulbs`);
        if (response.data?.data && response.data.data.length > 0) setUlbList(response.data.data);
      } catch (error) {
        console.error("Failed to fetch ULBs:", error);
      } finally {
        setLoadingUlbs(false);
      }
    };
    fetchUlbs();
  }, []);

  const goToPreviousTab = () => {
    const tabs = ["form-builder", "access-report", "restriction", "frequency", "workflow", "sla"];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1]);
  };

  const goToNextTab = () => {
    const tabs = ["form-builder", "access-report", "restriction", "frequency", "workflow", "sla"];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1]);
  };

  const uid = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

  const handleAddField = (fieldType) => {
    const newField = {
      id: uid(),
      type: fieldType,
      enLabel: fieldType === "text" ? "Text Field" :
        fieldType === "number" ? "Number Field" :
          fieldType === "date" ? "Date Field" :
            fieldType === "checkbox" ? "Checkbox Field" :
              fieldType === "radio" ? "Radio Field" :
                fieldType === "heading" ? "Heading" :
                  fieldType === "paragraph" ? "Paragraph" :
                    fieldType === "image" ? "Image Field" :
                      fieldType === "row" ? "2 Columns" :
                        fieldType === "graph" ? "Graph Field" :
                          fieldType === "comparison" ? "Comparison Field" : `${fieldType} Field`,
      enPlaceholder: fieldType === "text" ? "Enter text..." : "",
      enRequired: false,
      enOptions: (fieldType === "radio" || fieldType === "checkbox") ? ["Yes", "No"] : [],
      enHelpText: "",
      mrLabel: fieldType === "text" ? "मजकूर फील्ड" :
        fieldType === "number" ? "संख्या फील्ड" :
          fieldType === "date" ? "तारीख फील्ड" :
            fieldType === "checkbox" ? "चेकबॉक्स" :
              fieldType === "radio" ? "रेडिओ बटन" :
                fieldType === "heading" ? "शीर्षक" :
                  fieldType === "paragraph" ? "परिच्छेद" :
                    fieldType === "image" ? "प्रतिमा" :
                      fieldType === "row" ? "२ स्तंभ" :
                        fieldType === "graph" ? "आलेख" :
                          fieldType === "comparison" ? "तुलना फील्ड" : `${fieldType} फील्ड`,
      mrPlaceholder: fieldType === "text" ? "मजकूर प्रविष्ट करा..." : "",
      mrRequired: false,
      mrOptions: (fieldType === "radio" || fieldType === "checkbox") ? ["होय", "नाही"] : [],
      mrHelpText: "",
      label: fieldType === "text" ? "Text Field" :
        fieldType === "number" ? "Number Field" :
          fieldType === "date" ? "Date Field" :
            fieldType === "checkbox" ? "Checkbox Field" :
              fieldType === "radio" ? "Radio Field" :
                fieldType === "heading" ? "Heading" :
                  fieldType === "paragraph" ? "Paragraph" :
                    fieldType === "image" ? "Image Field" :
                      fieldType === "row" ? "2 Columns" :
                        fieldType === "graph" ? "Graph Field" :
                          fieldType === "comparison" ? "Comparison Field" : `${fieldType} Field`,
      placeholder: fieldType === "text" ? "Enter text..." : "",
      required: false,
      options: (fieldType === "radio" || fieldType === "checkbox") ? ["Yes", "No"] : [],
      helpText: "",
      position: nodes.length,
      parentId: null,
      colName: null
    };

    if (fieldType === "radio") {
      newField.isRadioGroup = true;
      newField.radioOptions = ["Yes", "No"];
    }
    if (fieldType === "checkbox") {
      newField.isCheckboxGroup = true;
      newField.checkboxOptions = ["Yes", "No"];
    }
    if (fieldType === "graph") {
      newField.graphType = "bar";
      newField.xAxisField = "";
      newField.yAxisField = "";
      newField.xLabel = "X-Axis";
      newField.yLabel = "Y-Axis";
      newField.multipleYAxis = false;
      newField.yAxisFields = [];
    }
    if (fieldType === "comparison") {
      newField.isComparisonGroup = true;
      newField.comparisonOperator = "equals";
      newField.mainField = {
        id: uid(),
        type: "number",
        enLabel: "Main Value",
        enPlaceholder: "Enter main value",
        enRequired: false,
        mrLabel: "मुख्य मूल्य",
        mrPlaceholder: "मुख्य मूल्य प्रविष्ट करा",
        mrRequired: false,
        label: "Main Value",
        placeholder: "Enter main value",
        required: false,
      };
      newField.subFields = [{
        id: uid(),
        type: "number",
        arithmeticOperator: "add",
        enLabel: "Sub Value 1",
        enPlaceholder: "Enter value",
        enRequired: false,
        enHelpText: "",
        mrLabel: "उप-मूल्य १",
        mrPlaceholder: "मूल्य प्रविष्ट करा",
        mrRequired: false,
        mrHelpText: "",
        label: "Sub Value 1",
        placeholder: "Enter value",
        required: false,
        helpText: "",
      }];
    }
    if (fieldType === "arithmetic") {
      newField.isArithmeticGroup = true;
      newField.arithmeticOperator = "add";
      newField.arithmeticFields = [{
        id: uid(),
        type: "number",
        enLabel: "Value 1",
        enPlaceholder: "Enter value",
        enRequired: false,
        mrLabel: "मूल्य १",
        mrPlaceholder: "मूल्य प्रविष्ट करा",
        mrRequired: false,
        label: "Value 1",
        placeholder: "Enter value",
        required: false,
      }];
      newField.showResultBox = true;
      newField.resultField = {
        id: uid(),
        type: "number",
        enLabel: "Result",
        enPlaceholder: "Result",
        enRequired: false,
        mrLabel: "परिणाम",
        mrPlaceholder: "परिणाम",
        mrRequired: false,
        label: "Result",
        placeholder: "Result",
        required: false,
      };
    }
    if (fieldType === "row") {
      newField.children = { left: [], right: [] };
    }

    setNodes(prevNodes => [...prevNodes, newField]);
  };

  // Auto scroll to bottom when nodes length changes (new field added)
  useEffect(() => {
    if (nodes.length > 0) {
      const scrollToBottom = () => {
        const mainElement = document.querySelector('.dashboard-content-wrapper');
        if (mainElement) {
          mainElement.scrollTo({
            top: mainElement.scrollHeight,
            behavior: 'smooth'
          });
        }
      };
      setTimeout(scrollToBottom, 50);
      setTimeout(scrollToBottom, 150);
      setTimeout(scrollToBottom, 300);
    }
  }, [nodes.length]);

  const handlePreviewFromPalette = () => {
    if (!nodes || nodes.length === 0) {
      message.warning(language === 'english' ? "Add some fields first!" : "प्रथम काही फील्ड जोडा!");
      return;
    }
    setShowPreviewModal(true);
  };

  const handleTranslateForm = async () => {
    const rootFields = nodes.filter(f => !f.parentId);
    const updatedFields = await translateForm(rootFields);
    if (updatedFields && updatedFields.length > 0) {
      const allTranslatedFields = [];
      updatedFields.forEach(field => {
        allTranslatedFields.push(field);
        const subFields = nodes.filter(f => f.parentId === field.id);
        allTranslatedFields.push(...subFields);
      });
      setNodes(allTranslatedFields);
    }
  };

  const buildScheduleObject = () => {
    if (!enableFrequency || !frequencyType) return {};
    const scheduleObject = {};
    if (frequencyType === 'daily') {
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.dateFormat = scheduleConfig.dateFormat || "YYYY-MM-DD";
    }
    else if (frequencyType === 'weekly') {
      scheduleObject.weekday = scheduleConfig.weekday || "monday";
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.dateFormat = scheduleConfig.dateFormat || "YYYY-MM-DD";
    }
    else if (frequencyType === 'monthly') {
      scheduleObject.monthlySelectionType = scheduleConfig.monthlySelectionType || "date";
      if (scheduleConfig.monthlySelectionType === 'date') scheduleObject.date = scheduleConfig.date || 1;
      if (scheduleConfig.monthlySelectionType === 'day') {
        scheduleObject.dayOfWeek = scheduleConfig.dayOfWeek || "monday";
        scheduleObject.weekNumber = scheduleConfig.weekNumber || 1;
      }
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.monthlyDateFormat = scheduleConfig.monthlyDateFormat || "YYYY-MM";
    }
    else if (frequencyType === 'quarterly') {
      scheduleObject.selectionType = scheduleConfig.selectionType || "date";
      if (scheduleConfig.selectionType === 'date') scheduleObject.date = scheduleConfig.date || 1;
      if (scheduleConfig.selectionType === 'day') {
        scheduleObject.dayOfWeek = scheduleConfig.dayOfWeek || "monday";
        scheduleObject.weekNumber = scheduleConfig.weekNumber || 1;
      }
      scheduleObject.quarterlyEveryNMonths = scheduleConfig.quarterlyEveryNMonths || 3;
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.monthlyDateFormat = scheduleConfig.monthlyDateFormat || "YYYY-MM";
    }
    else if (frequencyType === 'half_yearly') {
      scheduleObject.selectionType = scheduleConfig.selectionType || "date";
      if (scheduleConfig.selectionType === 'date') scheduleObject.date = scheduleConfig.date || 1;
      if (scheduleConfig.selectionType === 'day') {
        scheduleObject.dayOfWeek = scheduleConfig.dayOfWeek || "monday";
        scheduleObject.weekNumber = scheduleConfig.weekNumber || 1;
      }
      scheduleObject.halfYearlyEveryNMonths = scheduleConfig.halfYearlyEveryNMonths || 6;
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.monthlyDateFormat = scheduleConfig.monthlyDateFormat || "YYYY-MM";
    }
    else if (frequencyType === 'yearly') {
      scheduleObject.selectionType = scheduleConfig.selectionType || "date";
      if (scheduleConfig.selectionType === 'date') {
        scheduleObject.date = scheduleConfig.date || 1;
        scheduleObject.month = scheduleConfig.month || 1;
      }
      if (scheduleConfig.selectionType === 'day') {
        scheduleObject.dayOfWeek = scheduleConfig.dayOfWeek || "monday";
        scheduleObject.weekNumber = scheduleConfig.weekNumber || 1;
        scheduleObject.month = scheduleConfig.month || 1;
      }
      scheduleObject.time = scheduleConfig.time || "09:00";
      scheduleObject.period = scheduleConfig.period || "this";
      scheduleObject.yearlyFormat = scheduleConfig.yearlyFormat || "YYYY";
    }
    return scheduleObject;
  };

  const saveCurrentTab = async () => {
    try {
      setSaving(true);
      
      const userStr = sessionStorage.getItem('user');
      let currentUserId = null;
      let currentUserName = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          currentUserId = userData.id || userData.user_id || userData.user?.id;
          currentUserName = userData.name || userData.username || userData.user?.username || userData.user?.name || 'Unknown User';
          console.log('👤 Current user for form creation:', { currentUserId, currentUserName });
        } catch(e) {
          console.error('Failed to parse user data:', e);
        }
      }
      
      let iconUrl = null;
      if (formIconUrl && (formIconUrl.startsWith('http://') || formIconUrl.startsWith('https://') || formIconUrl.startsWith('/dashboard-icons/'))) {
        iconUrl = formIconUrl;
      } else if (formIconUrl && formIconUrl.startsWith('data:image')) {
        iconUrl = await uploadIconImmediately(formIconUrl);
      } else {
        iconUrl = formIconUrl;
      }
      
      const scheduleObject = buildScheduleObject();
      const frequencyObject = enableFrequency ? {
        enabled: true,
        type: frequencyType,
        ...(frequencyType === 'custom' ? { custom: customFrequency } : { value: frequencyValue, unit: frequencyUnit }),
        schedule: Object.keys(scheduleObject).length > 0 ? scheduleObject : null
      } : { enabled: false };
      
      const formattedPermissions = {};
      Object.entries(permissions).forEach(([roleId, perm]) => {
        formattedPermissions[roleId] = {
          can_access: perm.can_access || false,
          can_view: perm.can_view || false,
          can_edit: perm.can_edit || false,
          can_submit: perm.can_submit || false
        };
        if (perm.selectedIds && Array.isArray(perm.selectedIds)) {
          formattedPermissions[roleId].selectedIds = perm.selectedIds;
          formattedPermissions[roleId].selectedCount = perm.selectedIds.length;
        }
        if (perm.selectedDesks && Array.isArray(perm.selectedDesks)) {
          formattedPermissions[roleId].selectedDesks = perm.selectedDesks;
          formattedPermissions[roleId].selectedDesksCount = perm.selectedDesks.length;
        }
        if (perm.modalType) formattedPermissions[roleId].modalType = perm.modalType;
      });
      
      // ✅ CRITICAL: Create separate access_control object
      const accessControlData = { ...formattedPermissions };
      
      const formData = {
        form_name: formName,
        schema: nodes,
        workflow: workflow,
        sla: sla,
        permissions: formattedPermissions,
        access_control: accessControlData,  // 👈 ADD THIS
        report_config: reportConfig,
        language: language,
        form_language_mode: formLanguageMode,
        entry_type: entryType,
        frequency: frequencyObject,
        max_entries_per_user: maxEntriesPerUser,
        selected_ulb_classes: selectedULBClasses,
        selected_ulbs: targetKeys.map(key => parseInt(key)),
        form_icon: iconUrl || null,
        form_icon_type: iconUrl ? 'image' : (formIconUrl && !formIconUrl.startsWith('data:image') && !formIconUrl.startsWith('http') ? 'emoji' : null),
        form_icon_emoji: (!iconUrl && formIconUrl && !formIconUrl.startsWith('data:image') && !formIconUrl.startsWith('http')) ? formIconUrl : null,
        created_by: currentUserId,
        created_by_name: currentUserName
      };
      
      let url = `${API_BASE}/forms`;
      let method = "post";
      if (selectedFormId) {
        url = `${API_BASE}/forms/${selectedFormId}`;
        method = "put";
      }
      
      const response = await axios[method](url, formData);
      
      if (!selectedFormId) setSelectedFormId(response.data?.data?.id);
      if (response.data?.data?.form_icon) {
        const newIconUrl = getFullImageUrl(response.data.data.form_icon);
        setFormIconUrl(newIconUrl);
        setFormIcon(newIconUrl);
      }
      setApprovalStatus('pending');
      message.success("Form saved successfully!");
    } catch (error) {
      console.error('Save error:', error);
      message.error(error.response?.data?.error || "Failed to save form");
    } finally {
      setSaving(false);
    }
  };

  const saveAllTabs = async () => {
    try {
      setSavingAll(true);
      
      const userStr = sessionStorage.getItem('user');
      let currentUserId = null;
      let currentUserName = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          currentUserId = userData.id || userData.user_id || userData.user?.id;
          currentUserName = userData.name || userData.username || userData.user?.username || userData.user?.name || 'Unknown User';
          console.log('👤 Current user for form creation:', { currentUserId, currentUserName });
        } catch(e) {
          console.error('Failed to parse user data:', e);
        }
      }
      
      let iconUrl = null;
      if (formIconUrl && (formIconUrl.startsWith('http://') || formIconUrl.startsWith('https://') || formIconUrl.startsWith('/dashboard-icons/'))) {
        iconUrl = formIconUrl;
      } else if (formIconUrl && formIconUrl.startsWith('data:image')) {
        iconUrl = await uploadIconImmediately(formIconUrl);
      } else {
        iconUrl = formIconUrl;
      }
      
      let finalFormName = formName;
      if (language === "marathi" && !formName.includes("(Marathi)") && !formName.includes("(मराठी)")) {
        finalFormName = `${formName} (मराठी)`;
      }
      
      const scheduleObject = buildScheduleObject();
      const frequencyObject = enableFrequency ? {
        enabled: true,
        type: frequencyType,
        ...(frequencyType === 'custom' ? { custom: customFrequency } : { value: frequencyValue, unit: frequencyUnit }),
        schedule: Object.keys(scheduleObject).length > 0 ? scheduleObject : null
      } : { enabled: false };
      
      const formattedPermissions = {};
      Object.entries(permissions).forEach(([roleId, perm]) => {
        formattedPermissions[roleId] = {
          can_access: perm.can_access || false,
          can_view: perm.can_view || false,
          can_edit: perm.can_edit || false,
          can_submit: perm.can_submit || false
        };
        if (perm.selectedIds && Array.isArray(perm.selectedIds)) {
          formattedPermissions[roleId].selectedIds = perm.selectedIds;
          formattedPermissions[roleId].selectedCount = perm.selectedIds.length;
        }
        if (perm.selectedDesks && Array.isArray(perm.selectedDesks)) {
          formattedPermissions[roleId].selectedDesks = perm.selectedDesks;
          formattedPermissions[roleId].selectedDesksCount = perm.selectedDesks.length;
        }
        if (perm.modalType) formattedPermissions[roleId].modalType = perm.modalType;
      });
      
      // ✅ CRITICAL: Create separate access_control object
      const accessControlData = { ...formattedPermissions };
      
      const allData = {
        form_name: finalFormName,
        schema: nodes,
        workflow: workflow,
        sla: sla,
        permissions: formattedPermissions,
        access_control: accessControlData,  // 👈 ADD THIS
        report_config: reportConfig,
        language: language,
        form_language_mode: formLanguageMode,
        entry_type: entryType,
        frequency: frequencyObject,
        max_entries_per_user: maxEntriesPerUser,
        selected_ulb_classes: selectedULBClasses,
        selected_ulbs: targetKeys.map(key => parseInt(key)),
        form_icon: iconUrl || null,
        form_icon_type: iconUrl ? 'image' : (formIconUrl && !formIconUrl.startsWith('data:image') && !formIconUrl.startsWith('http') ? 'emoji' : null),
        form_icon_emoji: (!iconUrl && formIconUrl && !formIconUrl.startsWith('data:image') && !formIconUrl.startsWith('http')) ? formIconUrl : null,
        created_by: currentUserId,
        created_by_name: currentUserName
      };
      
      let url = `${API_BASE}/forms`;
      let method = "post";
      if (selectedFormId) {
        url = `${API_BASE}/forms/${selectedFormId}`;
        method = "put";
      }
      
      const response = await axios[method](url, allData);
      
      if (!selectedFormId) setSelectedFormId(response.data?.data?.id);
      if (response.data?.data?.form_icon) {
        const newIconUrl = getFullImageUrl(response.data.data.form_icon);
        setFormIconUrl(newIconUrl);
        setFormIcon(newIconUrl);
      }
      setApprovalStatus('pending');
      message.success("All form data saved successfully!");
      setFormName(finalFormName);
      setTimeout(() => {
        window.location.href = '/super-admin/form-templates';
      }, 1000);
    } catch (error) {
      console.error('Save all error:', error);
      message.error(error.response?.data?.error || "Failed to save form");
    } finally {
      setSavingAll(false);
    }
  };

  const getTabCompletionStatus = () => {
    const tabs = ["form-builder", "access-report", "restriction", "frequency", "workflow", "sla"];
    const currentIndex = tabs.indexOf(activeTab);
    return {
      currentTab: activeTab,
      currentIndex: currentIndex + 1,
      totalTabs: tabs.length,
      tabNames: {
        "form-builder": language === "marathi" ? "फॉर्म बिल्डर" : "Form Builder",
        "access-report": language === "marathi" ? "ॲक्सेस रिपोर्ट" : "Access Report",
        "restriction": language === "marathi" ? "प्रवेश नियंत्रण" : "Access Control",
        "frequency": language === "marathi" ? "वारंवारता" : "Frequency",
        "workflow": language === "marathi" ? "वर्कफ्लो" : "Workflow",
        "sla": "SLA"
      }
    };
  };

  const tabStatus = getTabCompletionStatus();

  const handleFormLoad = (formId) => {
    loadFormById(formId);
  };

  const loadFormById = async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/forms/${id}`);
      const data = res.data?.data;
      if (data) {
        if (data.schema && Array.isArray(data.schema) && data.schema.length > 0) setNodes(data.schema);
        else setNodes([]);
        setFormName(data.form_name || "Untitled Document");
        setSelectedFormId(data.id);
        // ✅ CRITICAL: Load permissions from access_control first
        if (data.access_control && typeof data.access_control === 'object' && Object.keys(data.access_control).length > 0) {
          console.log("✅ Loading permissions from access_control:", data.access_control);
          setPermissions(data.access_control);
        } else if (data.permissions && typeof data.permissions === 'object' && Object.keys(data.permissions).length > 0) {
          console.log("✅ Loading permissions from permissions:", data.permissions);
          setPermissions(data.permissions);
        } else {
          setPermissions({});
        }
        setWorkflow(data.workflow || { type: "none", sequence: [] });
        setSla(data.sla || {});
        setLanguage(data.language || "english");
        if (data.form_icon) {
          const fullUrl = getFullImageUrl(data.form_icon);
          setFormIcon(fullUrl);
          setFormIconUrl(fullUrl);
        } else if (data.form_icon_emoji) {
          setFormIcon(data.form_icon_emoji);
          setFormIconUrl(data.form_icon_emoji);
        }
        if (data.form_language_mode) setFormLanguageMode(data.form_language_mode);
        if (data.entry_type) setEntryType(data.entry_type);
        if (data.approval_status) setApprovalStatus(data.approval_status);
        if (data.report_config) setReportConfig(data.report_config);
        if (data.selected_ulb_classes) setSelectedULBClasses(data.selected_ulb_classes);
        if (data.selected_ulbs) {
          setSelectedUlbs(data.selected_ulbs);
          setTargetKeys(data.selected_ulbs.map(id => id.toString()));
        }
        if (data.frequency) {
          setEnableFrequency(data.frequency.enabled || false);
          if (data.frequency.enabled) {
            setFrequencyType(data.frequency.type || null);
            setCustomFrequency(data.frequency.custom || "");
            setFrequencyUnit(data.frequency.unit || "days");
            setFrequencyValue(data.frequency.value || 1);
            if (data.frequency.schedule) setScheduleConfig(prev => ({ ...prev, ...data.frequency.schedule }));
          }
        }
        if (data.max_entries_per_user !== undefined) setMaxEntriesPerUser(data.max_entries_per_user);
      }
    } catch (error) {
      console.error('Load form error:', error);
      message.error("Failed to load form");
    }
  };

  const showPreview = () => {
    if (!nodes || nodes.length === 0) {
      message.warning("Add some content first!");
      return;
    }
    setShowPreviewModal(true);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Navbar
        onMenuClick={toggleSidebar}
        language={language}
        onLanguageChange={(newLang) => {
          setLanguage(newLang);
        }}
      />

      <div style={{ display: "flex" }}>
        <main
          className="dashboard-content-wrapper"
          style={{
            flex: 1,
            marginLeft: 0,
            transition: "margin-left 0.3s ease",
            padding: "0px 24px",
            paddingTop: "110px",
            minHeight: "calc(150vh - 100px)",
            position: "relative",
            overflowY: "auto",
            height: "calc(120vh - 80px)",
            width: "100%"
          }}
        >
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "12px",
                    border: formIconUrl ? "2px solid #113F67" : "2px dashed #d9d9d9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fafafa",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    marginBottom: "8px",
                    position: "relative"
                  }}
                  onClick={() => document.getElementById('formIconUploadInput').click()}
                >
                  {uploadingIcon ? (
                    <Spin size="small" />
                  ) : formIconUrl && !iconLoadError ? (
                    (() => {
                      const isImageUrl = formIconUrl.startsWith('data:image') ||
                                        formIconUrl.startsWith('http://') ||
                                        formIconUrl.startsWith('https://') ||
                                        formIconUrl.startsWith('/dashboard-icons/');
                      if (isImageUrl) {
                        return (
                          <img
                            src={formIconUrl}
                            alt="Form Icon"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={() => setIconLoadError(true)}
                            onLoad={() => setIconLoadError(false)}
                          />
                        );
                      } else {
                        return <span style={{ fontSize: "40px" }}>{formIconUrl}</span>;
                      }
                    })()
                  ) : formIconUrl && iconLoadError ? (
                    <span style={{ fontSize: "40px" }}>📄</span>
                  ) : (
                    <PlusOutlined style={{ fontSize: 28, color: "#999" }} />
                  )}
                </div>
                <Space size={4}>
                  <Upload
                    accept="image/png,image/jpeg,image/jpg"
                    showUploadList={false}
                    beforeUpload={handleIconUpload}
                    maxCount={1}
                  >
                    <Button size="small" type="link" icon={<UploadOutlined />} loading={uploadingIcon}>
                      Upload
                    </Button>
                  </Upload>
                  {formIconUrl && (
                    <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={handleRemoveIcon}>
                      Remove
                    </Button>
                  )}
                </Space>
              </div>

              <div style={{ flex: 1 }}>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Form Name"
                  size="large"
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    border: "none",
                    padding: "8px 0",
                    background: "transparent"
                  }}
                />
                {approvalStatus && (
                  <Badge
                    status={
                      approvalStatus === 'approved' ? 'success' :
                      approvalStatus === 'rejected' ? 'error' : 'warning'
                    }
                    text={approvalStatus}
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>
            </div>

            <Badge
              count={`${tabStatus.currentIndex}/${tabStatus.totalTabs}`}
              style={{ backgroundColor: '#113F67' }}
            >
              <Text strong style={{ marginRight: 8 }}>
                {tabStatus.tabNames[activeTab]}
              </Text>
            </Badge>
          </div>

          <input
            type="file"
            id="formIconUploadInput"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleIconUpload(e.target.files[0]);
              }
            }}
          />

          {activeTab === "form-builder" && (
            <div style={{
              marginBottom: "24px",
              position: "sticky",
              top: "0",
              zIndex: 99,
              background: "#e6f7ff",
            }}>
              {isMobile ? (
                <CompactFieldTypesPalette
                  onAddField={handleAddField}
                  language={language}
                  onPreviewClick={handlePreviewFromPalette}
                  autoTranslate={autoTranslate}
                  onAutoTranslateToggle={() => setAutoTranslate(!autoTranslate)}
                  onTranslateForm={handleTranslateForm}
                  translating={translating}
                  onSave={saveCurrentTab}
                  saving={saving}
                  approvalStatus={approvalStatus}
                  currentUser={currentUser}
                  formCreator={formCreator}
                      formName={formName}
        nodes={nodes}
        showPreviewModal={showPreviewModal}
        setShowPreviewModal={setShowPreviewModal}
      />
              ) : (
                <FieldTypesPalette
                  onAddField={handleAddField}
                  language={language}
                  showExistingPalette={showExistingPalette}
                  existingColumns={existingColumns}
                  onPreviewClick={handlePreviewFromPalette}
                  autoTranslate={autoTranslate}
                  onAutoTranslateToggle={() => setAutoTranslate(!autoTranslate)}
                  onTranslateForm={handleTranslateForm}
                  translating={translating}
                  onSave={saveCurrentTab}
                  saving={saving}
                  approvalStatus={approvalStatus}
                  currentUser={currentUser}
                  formCreator={formCreator}
                  formName={formName}
        nodes={nodes}
        showPreviewModal={showPreviewModal}
        setShowPreviewModal={setShowPreviewModal}
                />
              )}
            </div>
          )}

          {activeTab === "frequency" && (
            <div style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              marginBottom: "24px"
            }}>
              <Card style={{ marginBottom: 24, border: "2px solid #1890ff", borderRadius: "8px" }}>
                <div style={{ padding: "16px" }}>
                  <Title level={5} style={{ marginBottom: 20, color: "#1890ff" }}>
                    <FormOutlined style={{ marginRight: 8 }} />
                    {language === 'english' ? 'Entry Type Configuration' : 'एंट्री प्रकार कॉन्फिगरेशन'}
                  </Title>
                  <Row gutter={24} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                      <Text strong>{language === 'english' ? 'Entry Type:' : 'एंट्री प्रकार:'}</Text>
                    </Col>
                    <Col span={16}>
                      <Radio.Group
                        value={entryType}
                        onChange={(e) => setEntryType(e.target.value)}
                        buttonStyle="solid"
                        size="large"
                      >
                        <Radio.Button value="single">
                          <Space><FormOutlined />{language === 'english' ? 'Single Entry' : 'एकल एंट्री'}</Space>
                        </Radio.Button>
                        <Radio.Button value="multi">
                          <Space><CopyFilled />{language === 'english' ? 'Multi Entry' : 'एकाधिक एंट्री'}</Space>
                        </Radio.Button>
                      </Radio.Group>
                    </Col>
                  </Row>
                  {entryType === 'multi' && (
                    <Row gutter={24}>
                      <Col span={8}>
                        <Text strong>{language === 'english' ? 'Max Entries Per User:' : 'प्रति यूजर कमाल एंट्री:'}</Text>
                      </Col>
                      <Col span={16}>
                        <Input
                          type="number"
                          min={0}
                          value={maxEntriesPerUser}
                          onChange={(e) => setMaxEntriesPerUser(parseInt(e.target.value) || 0)}
                          placeholder={language === 'english' ? "0 = Unlimited" : "० = अमर्यादित"}
                          style={{ width: 200 }}
                          size="large"
                        />
                        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: '12px' }}>
                          {language === 'english' ? 'Set 0 for unlimited entries' : 'अमर्यादित एंट्रीसाठी ० सेट करा'}
                        </Text>
                      </Col>
                    </Row>
                  )}
                </div>
              </Card>

              <Card style={{ border: "2px solid #52c41a", borderRadius: "8px" }}>
                <div style={{ padding: "16px" }}>
                  <Title level={5} style={{ marginBottom: 20, color: "#52c41a" }}>
                    <FieldTimeOutlined style={{ marginRight: 8 }} />
                    {language === 'english' ? 'Frequency Configuration' : 'वारंवारता कॉन्फिगरेशन'}
                  </Title>
                  <Row gutter={24} style={{ marginBottom: 20 }}>
                    <Col span={8}>
                      <Text strong>{language === 'english' ? 'Enable Frequency:' : 'वारंवारता सक्षम करा:'}</Text>
                    </Col>
                    <Col span={16}>
                      <Radio.Group value={enableFrequency} onChange={(e) => setEnableFrequency(e.target.value)}>
                        <Radio value={true}><Space><CheckCircleOutlined />{language === 'english' ? 'Yes' : 'होय'}</Space></Radio>
                        <Radio value={false}><Space><CloseOutlined />{language === 'english' ? 'No' : 'नाही'}</Space></Radio>
                      </Radio.Group>
                    </Col>
                  </Row>
                  {enableFrequency && (
                    <div style={{ marginTop: 16, padding: "20px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                      <Row gutter={24} style={{ marginBottom: 20 }}>
                        <Col span={8}>
                          <Text strong>{language === 'english' ? 'Frequency Type:' : 'वारंवारता प्रकार:'}</Text>
                        </Col>
                        <Col span={16}>
                          <Radio.Group value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)}>
                            <Space direction="vertical">
                              {FREQUENCY_OPTIONS.map(option => (
                                <Radio key={option.value} value={option.value}>
                                  <Space>{option.icon}<span>{option.label}</span></Space>
                                </Radio>
                              ))}
                            </Space>
                          </Radio.Group>
                        </Col>
                      </Row>
                      {frequencyType === 'custom' && (
                        <Row gutter={24} style={{ marginBottom: 20 }}>
                          <Col span={8}>
                            <Text strong>{language === 'english' ? 'Custom Frequency:' : 'सानुकूल वारंवारता:'}</Text>
                          </Col>
                          <Col span={16}>
                            <Input
                              value={customFrequency}
                              onChange={(e) => setCustomFrequency(e.target.value)}
                              placeholder={language === 'english' ? 'e.g., Every 2 weeks' : 'उदा., दर 2 आठवड्यांनी'}
                              size="large"
                            />
                          </Col>
                        </Row>
                      )}
                      {frequencyType && frequencyType !== 'custom' && (
                        <ScheduleConfiguration
                          frequencyType={frequencyType}
                          scheduleConfig={scheduleConfig}
                          onScheduleChange={handleScheduleConfigChange}
                          language={language}
                          disabled={false}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          <div
            ref={formBuilderContainerRef}
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              minHeight: "500px",
              marginBottom: "24px",
            }}
          >
            <FormBuilderContent
              location={location}
              activeTab={activeTab}
              formName={formName}
              setFormName={setFormName}
              formsList={formsList}
              loadingList={loadingList}
              selectedFormId={selectedFormId}
              setSelectedFormId={setSelectedFormId}
              nodes={nodes}
              setNodes={setNodes}
              originalFormData={originalFormData}
              setOriginalFormData={setOriginalFormData}
              onFormLoad={handleFormLoad}
              onPreview={showPreview}
              onSave={saveCurrentTab}
              saving={saving}
              language={language}
              setLanguage={setLanguage}
              permissions={permissions}
              setPermissions={setPermissions}
              workflow={workflow}
              setWorkflow={setWorkflow}
              sla={sla}
              setSla={setSla}
              onAddField={handleAddField}
              existingColumns={existingColumns}
              setExistingColumns={setExistingColumns}
              showExistingPalette={showExistingPalette}
              setShowExistingPalette={setShowExistingPalette}
              sbCollapsed={sbCollapsed}
              isMobile={isMobile}
              showPreview={showPreviewModal}
              setShowPreview={setShowPreviewModal}
              formLanguageMode={formLanguageMode}
              setFormLanguageMode={setFormLanguageMode}
              selectedULBClasses={selectedULBClasses}
              setSelectedULBClasses={setSelectedULBClasses}
              ulbList={ulbList}
              filteredUlbs={filteredUlbs}
              setFilteredUlbs={setFilteredUlbs}
              selectedUlbs={selectedUlbs}
              setSelectedUlbs={setSelectedUlbs}
              loadingUlbs={loadingUlbs}
              targetKeys={targetKeys}
              setTargetKeys={setTargetKeys}
              approvalStatus={approvalStatus}
              setApprovalStatus={setApprovalStatus}
              formCreator={formCreator}
              formCreatedAt={formCreatedAt}
              reviewedBy={reviewedBy}
              reviewedAt={reviewedAt}
              rejectionReason={rejectionReason}
              setRejectionReason={setRejectionReason}
              reviewerNotes={reviewerNotes}
              setReviewerNotes={setReviewerNotes}
              reportConfig={reportConfig}
              setReportConfig={setReportConfig}
              formIcon={formIcon}
              formIconUrl={formIconUrl}
              setFormIcon={setFormIcon}
              setFormIconUrl={setFormIconUrl}
            />
          </div>

          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginBottom: "20px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={goToPreviousTab}
                disabled={activeTab === "form-builder"}
                style={{ padding: "10px 24px" }}
              >
                {language === "marathi" ? "मागे" : "Previous"}
              </Button>

              <Space>
                {activeTab !== "sla" ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={async () => {
                      await saveCurrentTab();
                      goToNextTab();
                    }}
                    style={{
                      padding: "10px 24px",
                      background: "#113F67",
                      minWidth: "150px"
                    }}
                  >
                    {language === "marathi" ? "जतन करा आणि पुढे" : "Save & Next"}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={savingAll}
                    onClick={saveAllTabs}
                    style={{
                      padding: "10px 24px",
                      background: "#52c41a",
                      borderColor: "#52c41a",
                      minWidth: "180px",
                      fontWeight: "bold"
                    }}
                  >
                    {language === "marathi" ? "सर्व जतन करा आणि पूर्ण करा" : "Save All & Finish"}
                  </Button>
                )}
              </Space>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        ::-webkit-scrollbar { 
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        * {
          transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .field-type-chip,
        .existing-column-chip {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-font-smoothing: subpixel-antialiased;
          transition: transform 0.2s cubic-bezier(0.2, 0.9, 0.4, 1.1), box-shadow 0.2s ease !important;
        }
        .field-type-chip:hover,
        .existing-column-chip:hover {
          transform: translateY(-3px) !important;
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        }
        .ant-modal-mask {
          z-index: 1000;
        }
        .ant-modal-wrap {
          z-index: 1000;
        }
        main {
          scroll-behavior: smooth;
        }
        .dashboard-content-wrapper {
          transition: margin-left 0.3s ease, width 0.3s ease;
          background: #f8fafc;
          min-height: 100vh;
          width: 100%;
          margin-left: 0;
          overflow-x: hidden;
          position: relative;
          box-sizing: border-box;
        }
        .dashboard-content-wrapper.sidebar-collapsed {
          margin-left: 0;
          width: 100%;
        }
        @media (max-width: 767px) {
          .dashboard-content-wrapper,
          .dashboard-content-wrapper.sidebar-collapsed {
            margin-left: 0 !important;
            width: 100% !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }
        .dashboard-content-wrapper,
        .dashboard-content-wrapper.sidebar-collapsed,
        main.dashboard-content-wrapper {
          margin-left: 0 !important;
          width: 100% !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .dashboard-content-wrapper > div:first-child {
          padding-left: 0 !important;
          margin-left: 0 !important;
        }
        .ant-layout-content,
        .ant-spin-nested-loading,
        .ant-spin-container {
          margin-left: 0 !important;
          padding-left: 0 !important;
        }
        .dashboard-content-wrapper {
          padding-left: 24px !important;
          padding-right: 24px !important;
        }
        .dashboard-content-wrapper > div {
          padding-left: 12px !important;
          padding-right: 12px !important;
        }
      `}</style>
    </div>
  );
}

export default FormBuilderMain;